import { Box, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import BarChart from '../../../../components/charts/Bar';

export default function CostExport({ data }) {
    const { t } = useTranslation();
    const [selectedCategory, setSelectedCategory] = useState("");

    // Filter and map the data based on the selected category
    const costDisplayData = selectedCategory
    ? data
        .filter((item) => item.category === selectedCategory && item.min !== undefined && item.max !== undefined)
        .map(({ category, min, max }) => ({
            category,
            min: min / 1000, // Divide min by 1000
            max: max / 1000  // Divide max by 1000
        }))
    : data
        .filter((item) => item.min !== undefined && item.max !== undefined)
        .map(({ category, min, max }) => ({
            category,
            min: min / 1000, // Divide min by 1000
            max: max / 1000  // Divide max by 1000
        }));

    return (
        <Box sx={{ bgcolor: "#f9f9f9", fontSize: '0.875rem' }}> {/* Reduced base font size */}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    flexWrap: "wrap",
                    justifyContent: "center",
                    alignItems: "stretch",
                    maxWidth: "100%",
                    margin: "auto",
                    mt: { xs: 2, sm: 2 },
                    px: { xs: 1, sm: 2 },
                }}
            >
                <Box
                    id={`tlm-threat-1`}
                    sx={{
                        width: "210mm",
                        margin: "auto",
                        marginBottom: "20px",
                        pageBreakAfter: "always",
                        boxShadow: 3,
                        borderRadius: 2,
                        bgcolor: "#f9f9f9",
                        padding: "20px",
                        boxSizing: "border-box",
                        overflow: "hidden",
                    }}
                >
                    {/* Main Titles - UNCHANGED */}
                    <Typography
                        sx={{
                            textAlign: "start",
                            marginBottom: "10px",
                            fontWeight: "bold",
                            }}
                            variant='h5'
                    >
                        Section 4 : Impact Analysis
                    </Typography>

                    <Typography sx={{textAlign: "start",fontSize: "0.875rem", marginBottom: "10px" }}>
                        {`This section provides an analysis of the financial impact associated with various cybersecurity threat scenarios, focusing on loss magnitude and cost categories. The loss magnitude per threat scenario is calculated based on evaluations of four primary cost categories: Data Breach Costs (DBC), which include expenses related to compromised sensitive information; Ransom Amount Costs, reflecting potential ransom demands in ransomware attacks; Regulatory Fines, accounting for penalties under data protection laws like GDPR; and Unavailability Impact Costs (UIC), representing losses due to service disruptions. These calculations are informed by cybersecurity reports referenced in the document and mapped to the organization's profile, considering factors such as sector, revenue, size, geographical location, and client base.
 
The graph illustrates the estimated loss magnitude for each threat type, offering a theoretical perspective based on industry benchmarks and insights from referenced reports. The table complements this by detailing the specific cost ranges for each category, providing a clearer understanding of how these costs contribute to overall impact. While these evaluations are grounded in reliable data sources and benchmarks, they remain theoretical and require further refinement. Additional information about hidden costs—such as reputational damage or long-term operational disruptions—would be necessary to ensure a more comprehensive assessment of the organization's financial risk exposure.`
                        .replace(/\. /g, '.\n\n')}
                    </Typography>

                    <Typography
                        sx={{
                            textAlign: "start",
                            fontWeight: "bold",
                            }}
                            variant='h6'
                    >
                        4.1 Cost Categories
                    </Typography>

                    {/* Charts */}
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            justifyContent: "center",
                            alignItems: "flex-start",
                            gap: { xs: 2, sm: 4 },
                            mt: 4,
                            px: { xs: 1, sm: 2 },
                        }}
                    >
                        {/* BarChart Box */}
                        <Box
                            sx={{
                                boxShadow: 3,
                                borderRadius: 2,
                                p: { xs: 2, sm: 3 },
                                bgcolor: "background.paper",
                                width: { xs: "90%", sm: "90%", md: "90%" },
                                minHeight: "450px",
                                height: "auto",
                                textAlign: "center",
                            }}
                        >
                            <BarChart
                                horizontal={true}
                                data={costDisplayData}
                                xKey="category"
                                xLabel="Cost (K$)"
                                yLabel="Category"
                                title="Cost categories"
                                titleFontSize="0.875rem" // Added chart title font size
                                axisFontSize="0.75rem" // Added axis label font size
                            />
                        </Box>
                    </Box>

                    {/* Descriptions Section */}
                    {data.map((item, index) => (
                        <Box
                            key={index}
                            sx={{
                                display: "flex",
                                flexDirection: "row",
                                flexWrap: "wrap",
                                justifyContent: "center",
                                gap: 2,
                                mt: 4,
                                px: 4,
                            }}
                        >
                            <Box
                                sx={{
                                    boxShadow: 3,
                                    borderRadius: 2,
                                    p: { xs: 3, sm: 3 },
                                    bgcolor: "background.paper",
                                    width: { xs: "90%", sm: "90%", md: "90%" },
                                    // minHeight: "300px",
                                    height: "auto",
                                    textAlign: "start",
                                }}
                            >
                                <Typography variant="h6" sx={{ textAlign: "start", fontSize: '0.875rem', fontWeight: "bold",  }}> {/* Reduced from h5 */}
                                    {t(item?.category)}
                                </Typography>
                                <Typography sx={{ fontSize: '0.8125rem' }}> {/* Reduced body text size */}
                                    {item.justification}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
}